/**
 * CSV export trigger.
 *
 * Two behaviours here are requirements rather than polish:
 *
 *  1. The row count is IN THE LABEL. The operator sees the export size before
 *     committing, and it doubles as confirmation the filters did what they meant.
 *  2. Batching progress is visible. Export loops `.range()` in 1000-row batches
 *     (PostgREST caps every response at max_rows = 1000), so a large export takes
 *     several round-trips and would otherwise look frozen.
 *
 * If the hard cap truncates the result, we say so explicitly. A silent partial
 * export would recreate the exact bug this feature exists to fix.
 */

import { Download, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useToast } from '@/hooks/use-toast'
import { downloadCsv, exportFilename, toCsv, MAX_EXPORT_ROWS } from '@/lib/reporting/csv'
import { useReportExport } from '@/hooks/useReports'
import type {
  BookingFilters,
  QuoteFilters,
  ReportResource,
  TableSort,
} from '@limo/shared'

interface Props {
  readonly resource: ReportResource
  readonly filters: BookingFilters | QuoteFilters
  readonly sort: TableSort<string>
  readonly selectedColumns: readonly string[]
  /** Server-reported total for the current filters; drives the label. */
  readonly totalRows: number
  readonly disabled?: boolean
}

export function ExportButton({
  resource,
  filters,
  sort,
  selectedColumns,
  totalRows,
  disabled = false,
}: Props) {
  const { toast } = useToast()
  const { run, progress, isExporting } = useReportExport(resource)

  const noRows = totalRows === 0
  const noColumns = selectedColumns.length === 0

  const handleExport = async (): Promise<void> => {
    try {
      const result = await run(filters, sort)

      if (result.rows.length === 0) {
        toast({
          title: 'Nothing to export',
          description: 'No rows match the current filters.',
        })
        return
      }

      const csv = toCsv(resource, result.rows, selectedColumns)
      downloadCsv(exportFilename(resource), csv)

      if (result.truncated) {
        // Explicit, not silent — the file is NOT the complete result set.
        toast({
          title: 'Export truncated',
          description:
            `Exported the first ${result.rows.length.toLocaleString()} of ` +
            `${(result.total ?? 0).toLocaleString()} rows (limit ${MAX_EXPORT_ROWS.toLocaleString()}). ` +
            'Narrow the filters — for example by date range — to export the rest.',
          variant: 'destructive',
        })
        return
      }

      toast({
        title: 'Export ready',
        description:
          `${result.rows.length.toLocaleString()} rows × ${selectedColumns.length} columns ` +
          'downloaded as CSV.',
      })
    } catch (error) {
      // Surfacing the failure beats handing over a partial file that looks whole.
      toast({
        title: 'Export failed',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      })
    }
  }

  const label = (() => {
    if (isExporting) {
      const fetched = progress?.fetched ?? 0
      const total = progress?.total
      return total ? `Exporting… ${fetched.toLocaleString()} / ${total.toLocaleString()}` : 'Exporting…'
    }
    if (noRows) return 'Export'
    // Both dimensions of what you're about to get. The column count is here
    // because the picker's effect is otherwise invisible until the file opens.
    const rows = `${totalRows.toLocaleString()} row${totalRows === 1 ? '' : 's'}`
    const cols = `${selectedColumns.length} col${selectedColumns.length === 1 ? '' : 's'}`
    return `Export ${rows} · ${cols}`
  })()

  const tooltip = noColumns
    ? 'Select at least one column in "Export columns" first'
    : noRows
      ? 'No rows match the current filters'
      : `Downloads ${totalRows.toLocaleString()} rows with ${selectedColumns.length} columns as CSV`

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={() => void handleExport()}
      disabled={disabled || isExporting || noRows || noColumns}
      title={tooltip}
      // Announces batching progress to screen readers.
      role="status"
      aria-live="polite"
    >
      {isExporting ? (
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
      ) : (
        <Download className="mr-2 h-4 w-4" />
      )}
      {label}
    </Button>
  )
}
